import React, { useState, useEffect, useCallback, useMemo } from "react";
import { AUTH_BASE_URL } from "../../../config/config";
import PasswordStrengthBar from "react-password-strength-bar";
import { Link, useNavigate } from "react-router-dom";
import _ from "lodash";
import Loader from "../../shared/loader/Loader";
import AnalyticsService from "../../../services/analytics";

import "./register.scss";

const Register = ({ onLoginClick, onModalDismiss }) => {
  const navigate = useNavigate();

  const initialState = useMemo(() => ({
    first_name: "",
    last_name: "",
    email: "",
    date_of_birth: "",
    password: "",
    password_repeat: "",
    zip_code: "",
    living_abroad: false,
    country_id: 0,
    country_name: "",
    accept_terms: false,
    last_name_error: "",
    first_name_error: "",
    email_error: "",
    password_error: "",
    zip_code_error: "",
    accept_terms_error: "",
    country_error: "",
    countries: [],
    filtered_countries: [],
    active_country_search: false,
    social_login_success: false,
    is_loader_active: false,
  }), []);

  const [state, setState] = useState(initialState);

  useEffect(() => {
    AnalyticsService.sendPageView(window.location.pathname);
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  // Country list fetch
  const getCountryList = useCallback(() => {
    fetch(`${AUTH_BASE_URL}/country-list`)
      .then((res) => res.json())
      .then(
        (json) => {
          setState(prev => ({ ...prev, countries: json }));
        },
        (error) => {
          console.error(error);
        },
      );
  }, []);

  useEffect(() => {
    getCountryList();
  }, [getCountryList]);

  const validate = () => {
    let isValid = true;
    const newState = { ...state };

    if (newState.last_name.length < 3) {
      newState.last_name_error = "Helytelen vezetéknév!";
      isValid = false;
    } else {
      newState.last_name_error = "";
    }

    if (newState.first_name.length < 3) {
      newState.first_name_error = "Helytelen utónév!";
      isValid = false;
    } else {
      newState.first_name_error = "";
    }

    if (Number(newState.date_of_birth.length) !== 4) {
      newState.date_of_birth_error = "Helytelen születési év!";
      isValid = false;
    } else {
      newState.date_of_birth_error = "";
    }

    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (!re.test(String(newState.email).toLowerCase())) {
      newState.email_error = "Helytelen e-mail cím!";
      isValid = false;
    } else {
      newState.email_error = "";
    }

    if (newState.password.length < 8) {
      newState.password_error = "A jelszó hossza legalább 8 karakter!";
      isValid = false;
    } else {
      newState.password_error = "";
    }

    if (newState.password !== newState.password_repeat) {
      newState.password_error = "A jelszavak eltérőek!";
      isValid = false;
    } else if (newState.password.length >= 8) {
      newState.password_error = "";
    }

    if (newState.zip_code.toString().length < 4 && !newState.living_abroad) {
      newState.zip_code_error = "Helytelen irányítószám!";
      isValid = false;
    } else {
      newState.zip_code_error = "";
    }

    if (newState.country_name.toString().length < 4 && newState.living_abroad) {
      newState.country_error = "Helytelen országnév!";
      isValid = false;
    } else {
      newState.country_error = "";
    }

    if (newState.accept_terms !== true) {
      newState.accept_terms_error = "A feltételek elfogadása kötelező!";
      isValid = false;
    } else {
      newState.accept_terms_error = "";
    }

    setState(newState);
    return isValid;
  };

  const saveRegisterData = useCallback(() => {
    fetch(`${AUTH_BASE_URL}/register`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(state),
    })
      .then((res) => res.json())
      .then(
        (json) => {
          if (json.success === true) {
            navigate(`/validate-code/${json.sign}`);
            setState(initialState);
          } else {
            setState(prev => ({
              ...prev,
              email_error: json.reason,
              is_loader_active: false,
            }));
          }
        },
        (error) => {
          setState(prev => ({
            ...prev,
            is_loader_active: false,
          }));
        },
      );
  }, [state, navigate, initialState]);

  const handleSubmit = (event) => {
    event.preventDefault();
    setState(prev => ({ ...prev, is_loader_active: true }));

    if (validate()) {
      saveRegisterData();
    } else {
      setState(prev => ({ ...prev, is_loader_active: false }));
    }
  };

  const handleChange = (event) => {
    const { id, type, value, checked } = event.target;
    setState(prev => ({
      ...prev,
      [id]: type === "checkbox" ? checked : value,
    }));
  };

  const handleEmailChange = (event) => {
    fetch(`${AUTH_BASE_URL}/check-email/${event.target.value}`)
      .then((res) => res.json())
      .then(
        (json) => {
          if (json.success === true) {
            setState(prev => ({ ...prev, email_error: "" }));
          } else {
            setState(prev => ({ ...prev, email_error: json.reason }));
          }
        },
        (error) => {
          console.error(error);
        },
      );
  };

  const getZipCode = (event) => {
    if (event.target.value.length === 4) {
      fetch(`${AUTH_BASE_URL}/zip-list/${event.target.value}`)
        .then((res) => res.json())
        .then(
          (json) => {
            setState(prev => ({
              ...prev,
              zip_code: `${json[0].zip} ${json[0].city}`,
            }));
          },
          (error) => {
            console.error(error);
          },
        );
    }
  };

  const handleCountryChange = (event) => {
    const filteredArr = _.filter(state.countries, (o) =>
      o.name.toLowerCase().includes(event.target.value.toLowerCase())
    );

    const newState = {
      filtered_countries: filteredArr,
      country_name: event.target.value,
    };

    if (filteredArr.length === 1) {
      newState.country_id = filteredArr[0].id;
      newState.country_error = "";
      newState.active_country_search = true;
    } else if (filteredArr.length === 0) {
      newState.country_error = "Nem létező országot adtál meg!";
      newState.active_country_search = false;
    } else if (filteredArr.length > 1) {
      newState.country_error = "Nem létező országot adtál meg!";
      newState.active_country_search = true;
    }

    setState(prev => ({ ...prev, ...newState }));
  };

  const modifyCountryValue = (event, country_id, country_name) => {
    setState(prev => ({
      ...prev,
      country_id,
      country_name,
      active_country_search: false,
    }));
  };

  const activateCountrySearch = () => {
    setState(prev => ({ ...prev, active_country_search: true }));
  };

  return (
    <div className="register-container">
      <h2 className="h2">Regisztráció</h2>
      <p className="register-description">Regisztrálj és két perc múlva már rendelhetsz a Honvéd Shopból!</p>
      <p className="register-description">Felhasználói fiókodat aktiválnod kell, mielőtt bejelentkeznél az oldalra, minden ehhez kapcsolódó tovább információt visszaigazoló e-mail-ünk tartalmaz, melyet a rendszer automatikusan kiküld az általad megadott címre.</p>
      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group col-md-6">
            <label htmlFor="last_name">
              Vezetéknév
              <span>*</span>
            </label>
            <input
              type="text"
              className="form-control"
              id="last_name"
              value={state.last_name}
              onChange={handleChange}
            />
            <div className="validation_error">{state.last_name_error}</div>
          </div>
          <div className="form-group col-md-6">
            <label htmlFor="first_name">
              Keresztnév
              <span>*</span>
            </label>
            <input
              type="text"
              className="form-control"
              id="first_name"
              value={state.first_name}
              onChange={handleChange}
            />
            <div className="validation_error">{state.first_name_error}</div>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group col-md-6">
            <label htmlFor="email">
              E-mail cím
              <span>*</span>
            </label>
            <input
              type="text"
              className="form-control"
              id="email"
              value={state.email}
              onChange={handleChange}
              onBlur={handleEmailChange}
            />
            <div className="validation_error">{state.email_error}</div>
          </div>
          <div className="form-group col-md-6">
            <label htmlFor="first_name">
              Születési év
              <span>*</span>
            </label>
            <input
              type="text"
              className="form-control"
              id="date_of_birth"
              value={state.date_of_birth}
              onChange={handleChange}
            />
            <div className="validation_error">{state.date_of_birth_error}</div>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group col-md-6">
            <label htmlFor="password">
              Jelszó
              <span>*</span>
            </label>
            <input
              type="password"
              className="form-control"
              id="password"
              autoComplete="off"
              value={state.password}
              onChange={handleChange}
            />
            <PasswordStrengthBar
              password={state.password}
              className={(state.password.length === 0) ? "hidden" : ""}
              scoreWords={["gyenge", "gyenge", "közepes", "jó", "erős"]}
              shortScoreWord="túl rövid"
            />
            <div className="validation_error">{state.password_error}</div>
          </div>
          <div className="form-group col-md-6">
            <label htmlFor="password_repeat">
              Jelszó újra
              <span>*</span>
            </label>
            <input
              type="password"
              className="form-control"
              id="password_repeat"
              autoComplete="off"
              value={state.password_repeat}
              onChange={handleChange}
            />
          </div>
        </div>
        {!state.living_abroad && (
          <div className="form-row">
            <div className="form-group col-md-6">
              <label htmlFor="zip_code">
                Irányítószám
                <span>*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="zip_code"
                autoComplete="chrome-off"
                value={state.zip_code}
                onKeyUp={getZipCode}
                onChange={handleChange}
              />
              <div className="validation_error">{state.zip_code_error}</div>
            </div>
          </div>
        )}
        {state.living_abroad && (
          <div className="form-row">
            <div className="form-group col-md-6">
              <label htmlFor="country_id">
                Ország
                <span>*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="country_id"
                value={state.country_name}
                onChange={handleCountryChange}
                onFocus={activateCountrySearch}
              />
              <div className="validation_error">{state.country_error}</div>
              {state.living_abroad && (state.country_name.length > 2) && state.active_country_search && (
                <div className="predictive-country-list">
                  {state.filtered_countries.map((item) => (
                    <button
                      type="button"
                      key={item.id}
                      onClick={(e) => modifyCountryValue(e, item.id, item.name)}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        <div className="form-group form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="living_abroad"
            checked={state.living_abroad}
            onChange={handleChange}
          />
          <label className="form-check-label" htmlFor="living_abroad"><small>Külföldön lakom</small></label>
        </div>
        <div className="form-group form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="accept_terms"
            checked={state.accept_terms}
            onChange={handleChange}
          />
          <label className="form-check-label" htmlFor="accept_terms">
            <small>
              Kijelentem, hogy az
              <Link to="/tartalom/aszf">
                <u>
                  Általános
                  Szerződési Feltételeket
                </u>
              </Link>
              {" "}
              és az
              {" "}
              <Link to="/tartalom/adatvedelem"><u>Adatvédelmi tájékoztatót</u></Link>
              {" "}
              megismertem és elfogadom, s
              abban szereplő adatkezelésekhez hozzájárulok.
            </small>
          </label>
          <div className="validation_error">{state.accept_terms_error}</div>
        </div>
        <div className="text-center">
          <button type="submit" className="btn btn-primary" disabled={state.is_loader_active}>Regisztrálok</button>
          {state.is_loader_active && <Loader />}
        </div>
      </form>
      <div className="text-center existing-account">
        <p>
          Már van tagságod?{" "}
          <span className="link" onClick={onLoginClick}><strong><u>Jelentkezz be itt!</u></strong></span>
        </p>
      </div>
    </div>
  );
};

export default Register;
